




























import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import mapboxgl, { MapMouseEvent, CameraOptions } from 'mapbox-gl';
import Bro from 'brototype';
import circleToPolygon from 'circle-to-polygon';

import '@/globals';
import { Favorite } from '@/constants';
import { Poi, PoiState } from '@/models/poi';
import { Route } from '@/models/route';
import { PoiFilterSet } from '@/models/filter';
import ListoryGeolocateControl from '@/geolocate';
import PoiCardSlider from '@/components/poi-card-slider.vue';
import FilterHelper from '@/services/filter-helper';
import MapPoiHelper from '@/services/map-poi-helper';
import MapPositionHelper from '@/services/map-position-helper';
import MapRouteHelper from '@/services/map-route-helper';
import { delay } from '@/utils';

@Component({
    components: {
        PoiCardSlider
    }
})
export default class MapPoiPage extends Vue {

    @Prop({ type: Array, default: () => Vue.prototype.$store.routes }) public routeSource: Route[];
    @Prop({ type: Array, default: () => Vue.prototype.$store.pois }) public poiSource: Poi[];
    @Prop(Object) public mainRoute?: Route;
    @Prop(Boolean) public disableLocalization: boolean;

    public routes = new Array<Route>();
    public pois = new Array<Poi>();

    public map?: mapboxgl.Map;

    public mapPos: CameraOptions = {
        center: [9.556633, 47.111808],
        zoom: 10,
        bearing: 0
    };

    public slider?: PoiCardSlider;

    public poisGeoJson: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: []
    };

    private initialized: boolean;
    private geolocateControl: ListoryGeolocateControl;
    private selectedPoiId: number = undefined;
    private filterSet: PoiFilterSet;
    private isFiltered: boolean = false;
    private isMapLoaded: boolean;

    private isPageInBackground: boolean = true;

    private cachedFlyTarget: [number, number];


    private activePoiSource: Poi[];
    private activeRouteSource: Route[];

    private static mapInstance: number = 0;
    private mapId: string;

    public async created() {
        MapPoiPage.mapInstance += 1;
        this.mapId = 'poi_map_id_' + MapPoiPage.mapInstance;

        await this.$store.waitUntilReady();

        this.filterSet = FilterHelper.createPoiFilterSet();
        this.updateFilterIcon();

        this.activePoiSource = this.poiSource || this.$store.pois;
        this.activeRouteSource = this.routeSource || this.$store.routes;

        if (this.$store.mapPosition) this.mapPos = this.$store.mapPosition;
    }

    public mounted() {
        this.slider = this.$refs.poicardslider as PoiCardSlider;

        this.$on('poiFilterApplied', this.onPoiFilterApplied);
        this.$store.$on('storeUpdated', this.onStoreUpdated);
        this.$store.$on('poiReadyToUnlock', this.onPoiReadyToUnlock);
        this.$store.$on('poiUnlocked', this.onPoiUnlocked);
    }

    public beforeDestroy() {
        this.$off('poiFilterApplied', this.onPoiFilterApplied);
        this.$store.$off('storeUpdated', this.onStoreUpdated);
        this.$store.$off('poiReadyToUnlock', this.onPoiReadyToUnlock);
        this.$store.$off('poiUnlocked', this.onPoiUnlocked);

        if (this.map) this.map.remove();
    }

    public async onAppearing() {
        MapPositionHelper.restoreMapPosition(this.map, this.$store);

        if (this.map && this.isMapLoaded) {
            this.mapResize();

            // check if favorites has changed other other reason to recalc stuff
            await this.handleMapChangeRequest();

            if (this.geolocateControl) {
                this.geolocateControl.setActive(true);
            }
        }
        else if (!this.initialized) {
            await this.$store.waitUntilReady();

            this.pois = (this.activePoiSource) ? Array.from(this.activePoiSource) : [];
            this.routes = (this.activeRouteSource) ? Array.from(this.activeRouteSource) : [];
            this.loadMap();

            this.initialized = true;
        }
    }

    public onAppearred() {
        this.isPageInBackground = false;
        if (this.cachedFlyTarget) {
            this.cachedFlyTo(this.cachedFlyTarget);
        }
    }

    public onDisappearing() {
        MapPositionHelper.saveMapPosition(this.map, this.$store);

        if (this.geolocateControl) {
            this.geolocateControl.setActive(false);
        }

        this.isPageInBackground = true;
    }

    public loadMap() {
        const mapStyle = process.env.VUE_APP_MAPBOX_STYLE;

        this.map = new mapboxgl.Map({
            container: this.mapId,
            style: mapStyle,
            center: this.mapPos.center,
            zoom: this.mapPos.zoom,
            bearing: this.mapPos.bearing,
            minZoom: 9,
            maxZoom: 18,
            maxBounds: [
                [9.339637, 46.908183], // South West
                [9.776214, 47.391206] // North East
            ],
        });

        this.map.on('load', (e) => this.mapLoaded());
        this.map.on('click', (e) => this.onMapClicked(e));
    }

    public async mapLoaded() {
        // had issues with window.resize -> changed to map.resize
        this.mapResize();

        await this.$store.waitUntilReady();
        this.loadRoutes();
        this.loadPois();
        this.selectFirstPoi();
        this.enableGeoposition();
        this.handleMapChangeRequest();

        if (this.mainRoute) {
            MapRouteHelper.focusRoute(this.map, this.mainRoute);
        }

        // this.map.loaded() ofter returns false... so we use our own flag
        this.isMapLoaded = true;
    }

    public loadRoutes() {
        const labelLayerId = MapPoiHelper.findFirstLabelLayerId(this.map);

        for (const route of this.activeRouteSource) {
            MapPoiHelper.addRoute(this.map, route, labelLayerId);
        }
    }

    public mapResize() {
        // Needed to fix layout issues on devices with safe areas
        this.map.resize();
    }

    public loadPois() {
        this.updatePoiGeoJson();
        this.map.addSource('pois', { type: 'geojson', data: this.poisGeoJson });
        MapPoiHelper.addPois(this.map, 'pois');
    }

    public selectFirstPoi() {
        if (this.pois.length > 0) {
            const poi = this.pois[0];
            this.updateSelectedPoi(poi.id);
        }
    }

    public enableGeoposition() {
        // Compass Orientation
        this.map.addControl(
            new mapboxgl.NavigationControl({
                showCompass: true,
                showZoom: false
            })
        );

        // Add No Touchripple effect to compass
        this.$$('button.mapboxgl-ctrl-compass').addClass('no-ripple');

        // Position Lock
        this.geolocateControl = new ListoryGeolocateControl({
            positionOptions: {
                enableHighAccuracy: this.$store.settings.highAccuracyLocation
            },
            trackUserLocation: true,
            showUserLocation: true,
            isAndroid: this.$f7.device.android
        });

        this.map.addControl(this.geolocateControl);

        setTimeout(() => {
            this.$store.geolocator.restart();
            this.geolocateControl.trigger(this.disableLocalization);
        }, 1000);
    }

    private async handleMapChangeRequest() {
        if (this.$store.mapChangeSourceRequest) {
            if (this.$store.mapShowFavorites) {
                await this.showFavoritePois();
            } else {
                await this.showAllPois();
            }

            this.$store.mapChangeSourceRequest = false;
        }
    }

    private async showAllPois() {
        const favoriteFilterSet = FilterHelper.createPoiFilterSet();
        const filteredPois = FilterHelper.getFilteredPois(this.$store, this.activePoiSource, favoriteFilterSet);
        await this.onPoiFilterApplied(favoriteFilterSet, filteredPois);
    }

    private async showFavoritePois() {
        const favoriteFilterSet = FilterHelper.createPoiFilterSet();
        favoriteFilterSet.favoriteFilters = [Favorite.FAVORITE];
        const filteredPois = FilterHelper.getFilteredPois(this.$store, this.activePoiSource, favoriteFilterSet);
        await this.onPoiFilterApplied(favoriteFilterSet, filteredPois);
    }

    private async refilterPois() {
        const filteredPois = FilterHelper.getFilteredPois(this.$store, this.activePoiSource, this.filterSet);
        await this.onPoiFilterApplied(this.filterSet, filteredPois);
    }

    public onStoreUpdated() {
        this.filterSet = FilterHelper.createPoiFilterSet();
        this.updateFilterIcon();

        this.activePoiSource = this.$store.pois;
        this.activeRouteSource = this.$store.routes;

        this.pois = Array.from(this.activePoiSource);
        this.routes = Array.from(this.activeRouteSource);
    }

    private async onPoiFilterApplied(filterSet: PoiFilterSet, filteredPois: Poi[], updateMapCamera: boolean = true) {
        this.filterSet = filterSet;
        this.pois = filteredPois;

        this.updatePoiGeoJson();

        // set selectedPoi id if selected poi does not exist in current set
        let selectedPoi: Poi;

        if (this.selectedPoiId) {
            selectedPoi = this.pois.find(f => f.id === this.selectedPoiId);
            if (!selectedPoi) {
                this.selectedPoiId = undefined;
            }
        }

        // if no poi is selected select the next one on the slider index or the first on the remaining set
        if (!selectedPoi && this.pois.length > 0) {
            const actualSliderIndex = this.slider.activeIndex;
            if (actualSliderIndex > 0 && actualSliderIndex < this.pois.length) {
                selectedPoi = this.pois[actualSliderIndex];
            }
            else {
                selectedPoi = this.pois[0];
            }
        }

        // update geojson
        if (selectedPoi) {
            this.updateSelectedPoi(selectedPoi.id);
        }

        this.updateFilterIcon();

        // doesn't work without a delay (don't now why)
        await delay(50);

        // check if selected poi is the one selected in carousel
        if (selectedPoi) {
            const indexOfSelected = this.pois.indexOf(selectedPoi);
            if (indexOfSelected >= 0) {
                this.slider.jumpToIndex(indexOfSelected);
                this.onPoiChangedFromSlider(selectedPoi, 13);
            }
        }
        else {
            // update finally poi feautre data source if non uof the pois is selected
            this.updatePoiMapDataSource();
        }
    }

    private async onPoiReadyToUnlock(poi: Poi) {
        this.poisGeoJson.features.forEach(feature => {
            if (feature.id === poi.id) {
                feature.properties.state = poi.state;
            }
        });

        const poiCardIndex = this.pois.findIndex(p => p.id === poi.id);
        if (poiCardIndex >= 0) {
            this.slider.jumpToIndex(poiCardIndex);
        }

        this.updatePoiMapDataSource();

        // refilter pois when poi state changed an poi state filter is active
        if (FilterHelper.isUnlockFilterModified(this.filterSet)) {
            await this.refilterPois();
        }
    }

    private async onPoiUnlocked(poi: Poi) {
        this.poisGeoJson.features.forEach(feature => {
            if (feature.id === poi.id) {
                feature.properties.state = poi.state;
            }
        });

        this.updatePoiMapDataSource();

        // refilter pois when poi state changed an poi state filter is active
        if (FilterHelper.isUnlockFilterModified(this.filterSet)) {
            await this.refilterPois();
        }
    }

    private onMapClicked(event: MapMouseEvent) {
        const features = this.map.queryRenderedFeatures(event.point, {
            layers: [
                'poiHalos',
                'poiDots',
                'poiHalosOrange',
                'poiDotsOrange',
                'poiHalosSelected',
                'poiDotsSelected',
                'poiOutlinesSelected',
            ]
        });
        if (features.length < 1) {
            return;
        }

        const poi = features[0];
        const poiId = poi.id;
        const poiIndex = this.pois.findIndex(p => p.id === poiId);
        if (poiIndex >= 0) {
            this.slider.jumpToIndex(poiIndex);
        }
    }

    private updateSelectedPoi(poiId) {
        // find old feature in geojson and disable it
        const selectedPoi = this.poisGeoJson.features.find(
            f => f.id === this.selectedPoiId
        );
        if (selectedPoi) {
            selectedPoi.properties.selected = false;
        }

        // find new feaure in geojson and enable it
        const poiToSelect = this.poisGeoJson.features.find(
            f => f.id === poiId
        );
        if (poiToSelect) {
            poiToSelect.properties.selected = true;
            this.selectedPoiId = poiId;
        } else {
            this.selectedPoiId = undefined;
        }

        this.updatePoiMapDataSource();
    }

    private updatePoiGeoJson() {
        this.poisGeoJson.features = MapPoiHelper.createPoisGeoJSON(this.pois);
    }

    private updatePoiMapDataSource() {
        if (!this.map) return;
        const poisSource = this.map.getSource('pois') as mapboxgl.GeoJSONSource;
        if (poisSource) poisSource.setData(this.poisGeoJson);
    }

    private updateFilterIcon() {
        this.isFiltered = FilterHelper.isPoiFilterSetModified(this.filterSet);
    }

    private sliderInitialized() {
        const poi = this.poiForSlide(this.slider.activeIndex);
        this.updateSelectedPoi(poi.id);
    }

    private poiCardChanged() {
        const poi = this.poiForSlide(this.slider.activeIndex);
        this.updateSelectedPoi(poi.id);
        this.onPoiChangedFromSlider(poi);
    }

    private poiCardTapped() {
        let index = 0;
        if (this.slider.activeIndex !== undefined) {
            index = this.slider.activeIndex;
        }

        const poi = this.poiForSlide(index);
        this.$f7router.navigate('/pois/' + poi.id);
    }

    private poiForSlide(poiIndex: number) {
        if (poiIndex < 0 || poiIndex > this.pois.length - 1) {
            console.error('POI index ' + poiIndex + ' not found in store');
            return undefined;
        }

        return this.pois[poiIndex];
    }

    private onPoiChangedFromSlider(poi: Poi, targetZoom: number = 15) {
        if (!this.map) return;

        const target: [number, number] = [
            poi.coordinates.longitude,
            poi.coordinates.latitude
        ];
        target[1] -= 0.002; // Slight offset to center point above card
        this.cachedFlyTo(target, targetZoom);
    }

    private cachedFlyTo(target: [number, number], targetZoom: number = 15) {
        this.cachedFlyTarget = null;

        if (target && targetZoom && this.map && this.isMapLoaded && !this.isPageInBackground) {
            this.map.flyTo({
                center: target,
                zoom: targetZoom,
                speed: 0.5
            });
        }
        else if (target) {
            this.cachedFlyTarget = target;
        }
    }

    private onPoiFilterClicked() {
        this.$f7router.navigate('/pois/filter/', {
            props: {
                poiSource: this.activePoiSource,
                filterSet: this.filterSet,
                eventTarget: this
            }
        });
    }
}
